/**
 * DataRequest — public /data-request page.
 * Renders a Privacy Data Subject Request (DSR) form covering CCPA/CPRA
 * + GDPR rights. Submissions land in the ``data_requests`` collection and
 * fire an admin alert + requester ack email. On success, we hand back the
 * ticket ID + track link so the requester can bookmark it.
 */
import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ShieldCheck, FileSearch, AlertCircle, CheckCircle2, Copy, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const REQUEST_TYPES = [
    { key: "access", label: "Access — send me a copy of my personal data" },
    { key: "delete", label: "Delete — erase my personal data" },
    { key: "correct", label: "Correct — fix inaccurate information" },
    { key: "port", label: "Port — export my data to another provider" },
    { key: "opt_out_sale", label: "Opt out of sale or sharing" },
    { key: "restrict", label: "Restrict — limit how you process my data" },
    { key: "other", label: "Other (describe below)" },
];

const SUBJECT_TYPES = [
    { key: "talent", label: "I have a Talent (professional) account" },
    { key: "employer", label: "I have an Employer account" },
    { key: "visitor", label: "I've never signed up — visitor only" },
    { key: "other", label: "Something else / not sure" },
];

const JURISDICTIONS = [
    { key: "ccpa", label: "California (CCPA / CPRA)" },
    { key: "gdpr", label: "EEA / UK / Switzerland (GDPR)" },
    { key: "other", label: "Elsewhere / not applicable" },
];

export default function DataRequest() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const [form, setForm] = useState({
        type: params.get("type") || "access",
        name: "",
        email: "",
        subject_type: "talent",
        jurisdiction: "ccpa",
        details: "",
        is_authorized_agent: false,
        agent_name: "",
        consent: false,
    });
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);

    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const buildTrackUrl = (id, token) => `${window.location.origin}/data-request/track?id=${encodeURIComponent(id)}&token=${encodeURIComponent(token)}`;

    const submit = async (e) => {
        e.preventDefault();
        if (!form.consent) {
            toast.error("Please confirm you are authorized to make this request.");
            return;
        }
        if (form.is_authorized_agent && !form.agent_name.trim()) {
            toast.error("Please enter your name as the authorized agent.");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/privacy/data-request", form);
            setResult({ id: data.id, token: data.track_token, status: data.status, created_at: data.created_at });
            toast.success("Privacy request submitted.");
        } catch (err) {
            const msg = err?.response?.data?.detail || "Something went wrong. Please try again or email privacy@simonaratalent.us.";
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    if (result) {
        const trackUrl = buildTrackUrl(result.id, result.token);
        return (
            <div className="container-app py-14" data-testid="data-request-success">
                <Helmet>
                    <title>Privacy request received · Simonara</title>
                    <meta name="robots" content="noindex" />
                </Helmet>
                <div className="max-w-2xl mx-auto card-soft p-8">
                    <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider">
                        <CheckCircle2 size={12} /> Request received
                    </div>
                    <h1 className="mt-4 font-heading font-semibold text-3xl tracking-tight">Your privacy request is open.</h1>
                    <p className="mt-3 text-muted-foreground">
                        We&apos;ve emailed a confirmation to <b className="text-foreground">{form.email}</b> along with your tracking link. Save the details below just in case.
                    </p>
                    <div className="mt-6 rounded-xl border border-border bg-muted/30 p-4 space-y-2 text-sm">
                        <div className="flex items-center gap-3"><span className="text-muted-foreground w-24">Ticket ID:</span> <code className="font-mono text-foreground" data-testid="dsr-ticket-id">{result.id}</code></div>
                        <div className="flex items-start gap-3">
                            <span className="text-muted-foreground w-24 shrink-0">Track URL:</span>
                            <a href={trackUrl} className="text-emerald-600 dark:text-emerald-400 hover:underline break-all" data-testid="dsr-track-url">{trackUrl}</a>
                            <button
                                type="button"
                                onClick={() => { navigator.clipboard.writeText(trackUrl); toast.success("Link copied."); }}
                                className="ml-auto shrink-0 text-muted-foreground hover:text-foreground"
                                data-testid="dsr-copy-btn"
                                aria-label="Copy track URL"
                            >
                                <Copy size={14} />
                            </button>
                        </div>
                    </div>
                    <p className="mt-5 text-xs text-muted-foreground leading-relaxed">
                        We respond within 10 business days on average (CCPA regulatory window: 45 days · GDPR: 30 days). If we need additional identity verification we&apos;ll email you before touching the data.
                    </p>
                    <div className="mt-6 flex flex-wrap gap-2">
                        <button type="button" onClick={() => navigate("/privacy")} className="btn-outline" data-testid="dsr-back-privacy">
                            Back to privacy policy
                        </button>
                        <a href={trackUrl} className="btn-green inline-flex items-center gap-2" data-testid="dsr-open-track">
                            Track status <ArrowRight size={14} />
                        </a>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container-app py-14" data-testid="data-request-page">
            <Helmet>
                <title>Submit a data request · Simonara Talent Solutions</title>
                <meta name="description" content="Submit a CCPA, CPRA, or GDPR data request — access, delete, correct, port, or opt out. Get a trackable ticket ID." />
                <link rel="canonical" href="/data-request" />
            </Helmet>

            <header className="max-w-3xl">
                <p className="overline inline-flex items-center gap-1"><FileSearch size={11} /> Privacy</p>
                <h1 className="mt-2 font-heading font-medium text-4xl sm:text-5xl tracking-tight text-foreground">
                    Submit a data request
                </h1>
                <p className="mt-4 text-muted-foreground text-lg">
                    Use this form to exercise your CCPA/CPRA or GDPR rights — access, delete, correct, port, opt-out, or restrict. Every submission gets a tracking ID and an email confirmation.
                </p>
                <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="rounded-xl border border-border bg-muted/30 p-3 text-xs">
                        <b className="text-foreground">Response time</b><br /> 10 business days on average
                    </div>
                    <div className="rounded-xl border border-border bg-muted/30 p-3 text-xs">
                        <b className="text-foreground">Legal window</b><br /> CCPA 45 days · GDPR 30 days
                    </div>
                    <div className="rounded-xl border border-border bg-muted/30 p-3 text-xs">
                        <b className="text-foreground">Cost to you</b><br /> Free. Always.
                    </div>
                </div>
            </header>

            <form onSubmit={submit} className="mt-10 max-w-3xl card-soft p-7 space-y-6" data-testid="data-request-form">
                <div>
                    <label className="block text-sm font-semibold text-foreground mb-2" htmlFor="dsr-type">
                        What would you like us to do?
                    </label>
                    <select
                        id="dsr-type"
                        data-testid="dsr-type"
                        value={form.type}
                        onChange={(e) => setField("type", e.target.value)}
                        className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                    >
                        {REQUEST_TYPES.map((t) => (
                            <option key={t.key} value={t.key}>{t.label}</option>
                        ))}
                    </select>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-semibold text-foreground mb-2" htmlFor="dsr-name">Your name</label>
                        <input
                            id="dsr-name"
                            data-testid="dsr-name"
                            type="text"
                            required
                            value={form.name}
                            onChange={(e) => setField("name", e.target.value)}
                            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                            placeholder="Jane Doe"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-foreground mb-2" htmlFor="dsr-email">Email on file</label>
                        <input
                            id="dsr-email"
                            data-testid="dsr-email"
                            type="email"
                            required
                            value={form.email}
                            onChange={(e) => setField("email", e.target.value)}
                            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                            placeholder="you@example.com"
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-semibold text-foreground mb-2" htmlFor="dsr-subject">Account type</label>
                        <select
                            id="dsr-subject"
                            data-testid="dsr-subject-type"
                            value={form.subject_type}
                            onChange={(e) => setField("subject_type", e.target.value)}
                            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                        >
                            {SUBJECT_TYPES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-foreground mb-2" htmlFor="dsr-jurisdiction">Where do you live?</label>
                        <select
                            id="dsr-jurisdiction"
                            data-testid="dsr-jurisdiction"
                            value={form.jurisdiction}
                            onChange={(e) => setField("jurisdiction", e.target.value)}
                            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                        >
                            {JURISDICTIONS.map((j) => <option key={j.key} value={j.key}>{j.label}</option>)}
                        </select>
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-semibold text-foreground mb-2" htmlFor="dsr-details">
                        Additional details <span className="text-muted-foreground font-normal">(optional but helpful)</span>
                    </label>
                    <textarea
                        id="dsr-details"
                        data-testid="dsr-details"
                        rows={5}
                        maxLength={4000}
                        value={form.details}
                        onChange={(e) => setField("details", e.target.value)}
                        placeholder="Which specific records or fields does this cover? Any relevant date range? Anything else that will help us verify + respond quickly."
                        className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm leading-relaxed"
                    />
                    <div className="mt-1 text-[11px] text-muted-foreground text-right">{form.details.length} / 4000</div>
                </div>

                <div className="rounded-xl border border-border bg-muted/30 p-4 space-y-3">
                    <label className="flex items-start gap-2 cursor-pointer" data-testid="dsr-agent-toggle">
                        <input
                            type="checkbox"
                            checked={form.is_authorized_agent}
                            onChange={(e) => setField("is_authorized_agent", e.target.checked)}
                            className="mt-1"
                        />
                        <span className="text-sm text-foreground">I&apos;m submitting on behalf of someone else as an authorized agent.</span>
                    </label>
                    {form.is_authorized_agent && (
                        <input
                            type="text"
                            data-testid="dsr-agent-name"
                            value={form.agent_name}
                            onChange={(e) => setField("agent_name", e.target.value)}
                            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                            placeholder="Your legal name (as authorized agent)"
                        />
                    )}
                    <label className="flex items-start gap-2 cursor-pointer" data-testid="dsr-consent-toggle">
                        <input
                            type="checkbox"
                            required
                            checked={form.consent}
                            onChange={(e) => setField("consent", e.target.checked)}
                            className="mt-1"
                            data-testid="dsr-consent"
                        />
                        <span className="text-sm text-foreground">
                            I confirm I&apos;m authorized to make this request and the information above is accurate. I understand Simonara may need to verify my identity before acting.
                        </span>
                    </label>
                </div>

                <div className="rounded-xl border border-sky-500/40 bg-sky-500/10 p-4 flex items-start gap-3">
                    <ShieldCheck size={16} className="text-sky-600 dark:text-sky-400 mt-0.5 shrink-0" />
                    <p className="text-xs text-muted-foreground leading-relaxed">
                        You&apos;ll get an email with a tracking link once we receive this. Sensitive verification data is never touched until we&apos;ve confirmed your identity. See our <Link to="/privacy" className="underline hover:text-foreground">Privacy Policy</Link> for retention details.
                    </p>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                    <button
                        type="submit"
                        disabled={submitting}
                        data-testid="dsr-submit"
                        className="btn-green inline-flex items-center gap-2 disabled:opacity-60"
                    >
                        {submitting ? <><Loader2 size={14} className="animate-spin" /> Submitting…</> : <>Submit privacy request <ArrowRight size={14} /></>}
                    </button>
                    <Link to="/privacy" className="text-sm text-muted-foreground hover:text-foreground underline" data-testid="dsr-back-link">
                        Back to Privacy Policy
                    </Link>
                </div>

                <p className="text-[11px] text-muted-foreground">
                    Prefer email? Send us the same details at <a href="mailto:privacy@simonaratalent.us" className="underline hover:text-foreground">privacy@simonaratalent.us</a>. It&apos;s slower and untrackable — this form is better.
                </p>
            </form>
        </div>
    );
}

/** Simple public status tracker — /data-request/track?id=…&token=… */
export function DataRequestTracker() {
    const [params] = useSearchParams();
    const [state, setState] = useState({ loading: true, error: "", row: null });
    const id = params.get("id") || "";
    const token = params.get("token") || "";

    React.useEffect(() => {
        let mounted = true;
        if (!id || !token) {
            setState({ loading: false, error: "Missing tracking parameters.", row: null });
            return;
        }
        api.get(`/privacy/data-request/${encodeURIComponent(id)}`, { params: { token } })
            .then((r) => { if (mounted) setState({ loading: false, error: "", row: r.data }); })
            .catch((err) => {
                if (!mounted) return;
                setState({ loading: false, error: err?.response?.data?.detail || "Could not load this request.", row: null });
            });
        return () => { mounted = false; };
    }, [id, token]);

    const statusTone = (s) => ({
        open: "bg-sky-500/15 text-sky-700 dark:text-sky-400 border-sky-500/30",
        in_review: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30",
        identity_verification_needed: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30",
        resolved: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
        rejected: "bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30",
    }[s] || "bg-muted text-foreground border-border");

    const statusLabel = (s) => ({
        open: "Open — in queue",
        in_review: "In review",
        identity_verification_needed: "Awaiting identity verification",
        resolved: "Resolved",
        rejected: "Rejected",
    }[s] || s);

    return (
        <div className="container-app py-14" data-testid="dsr-tracker-page">
            <Helmet>
                <title>Track privacy request · Simonara</title>
                <meta name="robots" content="noindex" />
            </Helmet>
            <div className="max-w-2xl mx-auto card-soft p-7">
                <p className="overline inline-flex items-center gap-1"><FileSearch size={11} /> Privacy</p>
                <h1 className="mt-2 font-heading font-medium text-3xl tracking-tight text-foreground">Track your privacy request</h1>

                {state.loading && (
                    <div className="mt-6 py-8 flex justify-center" data-testid="dsr-tracker-loading">
                        <Loader2 className="animate-spin text-emerald-500" />
                    </div>
                )}

                {!state.loading && state.error && (
                    <div className="mt-6 rounded-xl border border-red-500/40 bg-red-500/10 p-4 flex items-start gap-3" data-testid="dsr-tracker-error">
                        <AlertCircle size={16} className="text-red-500 mt-0.5 shrink-0" />
                        <div className="text-sm text-foreground">
                            <div className="font-semibold">{state.error}</div>
                            <p className="mt-1 text-xs text-muted-foreground">Please check the link in your email or contact <a href="mailto:privacy@simonaratalent.us" className="underline">privacy@simonaratalent.us</a>.</p>
                        </div>
                    </div>
                )}

                {!state.loading && state.row && (
                    <div className="mt-6 space-y-4" data-testid="dsr-tracker-result">
                        <div className="rounded-xl border border-border bg-muted/30 p-4 space-y-2 text-sm">
                            <div className="flex items-center gap-2">
                                <span className="text-muted-foreground w-28">Ticket ID:</span>
                                <code className="font-mono text-foreground">{state.row.id}</code>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-muted-foreground w-28">Status:</span>
                                <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${statusTone(state.row.status)}`} data-testid="dsr-tracker-status">
                                    {statusLabel(state.row.status)}
                                </span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-muted-foreground w-28">Submitted:</span>
                                <span className="text-foreground">{new Date(state.row.created_at).toLocaleString()}</span>
                            </div>
                            {state.row.resolved_at && (
                                <div className="flex items-center gap-2">
                                    <span className="text-muted-foreground w-28">Resolved:</span>
                                    <span className="text-foreground">{new Date(state.row.resolved_at).toLocaleString()}</span>
                                </div>
                            )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                            You&apos;ll get an email at every status change. Questions? <a href="mailto:privacy@simonaratalent.us" className="underline hover:text-foreground">privacy@simonaratalent.us</a>.
                        </p>
                    </div>
                )}

                <div className="mt-6">
                    <Link to="/privacy" className="text-sm text-muted-foreground hover:text-foreground underline" data-testid="dsr-tracker-back">
                        ← Back to Privacy Policy
                    </Link>
                </div>
            </div>
        </div>
    );
}
